import { useCallback } from "react";

import { AccountViewer } from "components/form/graphql/types";
import useTypedContext from "hooks/useTypedContext";
import { Action, Stack } from "types/generated";
import { AccessResult, hasAtLeastOneRequiredAction, NO_ACCESS_MESSAGE } from "utils/access";
import { AccountContext } from "views/AccountWrapper";

import { checkTemplateDeploymentAccess } from "./helpers/checkTemplateDeploymentAccess";
import useHasStackWriteAccess from "./useHasStackWriteAccess";

const DELETE_CONFIG_ACCESS_TYPES = [Action.StackDeleteConfig];

const hasDeleteConfigAccess = (viewer: AccountViewer, spaceId: string) =>
  hasAtLeastOneRequiredAction(DELETE_CONFIG_ACCESS_TYPES, viewer, spaceId);

/**
 * Hook that returns a function to check if the current user can delete config from a stack.
 * Stacks created from a template deployment (blueprintDeployment) cannot have config deleted.
 */
const useCanDeleteStackConfig = (): ((stack: Stack) => AccessResult) => {
  const hasStackWriteAccess = useHasStackWriteAccess();
  const { viewer } = useTypedContext(AccountContext);

  return useCallback(
    (stack: Stack) => {
      const blueprintCheck = checkTemplateDeploymentAccess(stack);
      if (blueprintCheck) {
        return blueprintCheck;
      }

      if (hasStackWriteAccess(stack).result) {
        return { result: true };
      }

      if (hasDeleteConfigAccess(viewer, stack.spaceDetails?.id || stack.space)) {
        return { result: true };
      }

      return { result: false, message: NO_ACCESS_MESSAGE };
    },
    [hasStackWriteAccess, viewer]
  );
};

export default useCanDeleteStackConfig;
